import { Component, Input, OnDestroy } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { Subscription } from 'rxjs';
import { ColumnSettings } from 'src/app/core/model/column-settings';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { AuditProgramService } from 'src/app/core/service/audit-program.service';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-program-audit-details',
  imports: [CommonModule, TranslateModule, SharedModule],
  templateUrl: './program-audit-details.component.html',
  styleUrl: './program-audit-details.component.scss'
})
export class ProgramAuditDetailsComponent implements OnDestroy
{
  public currentProgramId: number;
  public currentRevisionId: number;
  public currentAuditType: number;    // 0=Program, 1=Leads/Groups, 2=Metrics
  public currentRevisionType: number; // 0=Create, 1=Update, 2=Delete
  public currentChangesOnly = false;
  public currentEnableAdvanced = false;

  public view: GridViewManager;
  public data = [];
  public pageSizes = [5, 10, 25, 50, 100];
  public count: number;
  public columnsConfig: ColumnSettings[];

  private messagingSubscription: Subscription;

  /**
   * Constructor
   */
  constructor(
    private auditProgramService: AuditProgramService,
    private translateService: TranslateService,
    private messageHandlerService: MessageHandlerService,
    private messengerService: MessengerService)
  {
    this.auditProgramService = auditProgramService;
    this.translateService = translateService;
    this.messageHandlerService = messageHandlerService;

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'refreshAuditProgramDetails')
        {
          this.initGridColumnsAndLoadData();
        }
      }
    });
  }

  @Input() public set programId(programId: number)
  {
    if (programId !== undefined)
    {
      this.currentProgramId = programId;
    }
  }

  @Input() public set revisionId(revisionId: number)
  {
    if (revisionId !== undefined)
    {
      this.currentRevisionId = revisionId;

      if (this.view !== undefined)
      {
        this.view.startLoading();
      }

      this.view = new GridViewManager(99999);
      this.view.setData(this.data);

      this.initGridColumnsAndLoadData();
    }
  }

  @Input() public set enableAdvanced(enableAdvanced: boolean)
  {
    if (enableAdvanced !== undefined)
    {
      this.currentEnableAdvanced = enableAdvanced;
    }
  }

  @Input() public set changesOnly(changesOnly: boolean)
  {
    if (changesOnly !== undefined)
    {
      this.currentChangesOnly = changesOnly;
    }
  }

  @Input() public set revisionType(revisionType: number)
  {
    if (revisionType !== undefined)
    {
      this.currentRevisionType = revisionType;
    }
  }

  @Input() public set auditType(auditType: number)
  {
    if (auditType !== undefined)
    {
      this.currentAuditType = auditType;
    }
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  /**
   * Get audit details for audit_channel 
   **/
  public getAuditProgramDetails(): void
  {
    this.auditProgramService.getAuditProgramDetails('program', this.currentProgramId, this.currentRevisionId, this.currentRevisionType).subscribe({
      next: (data) =>
      {
        this.count = data.length;
        this.data = [];
        let index = 0;
        data.forEach((d: any) =>
        {
          if (index === 0)
          {
            Object.keys(d).forEach(e =>
            {
              let propertyNameText = null;
              if (e !== 'id' && e !== 'revisionId')
              {
                if ((!this.currentChangesOnly) ||
                  ((this.currentChangesOnly && data.length === 2 && d[e] !== data[1][e])) || /* Update */
                  ((this.currentChangesOnly && data.length === 1)))                          /* Create */
                {
                  // Property Name
                  switch (e)
                  {
                    case 'active':
                      propertyNameText = this.translateService.instant('ACTIVE');
                      break;
                    case 'description':
                      propertyNameText = this.translateService.instant('DESCRIPTION');
                      break;
                    case 'name':
                      propertyNameText = this.translateService.instant('NAME');
                      break;
                    case 'categoryName':
                      propertyNameText = this.translateService.instant('CATEGORY');
                      break;
                    case 'organizationName':
                      propertyNameText = this.translateService.instant('ORGANIZATION');
                      break;
                    case 'programLead':
                      propertyNameText = this.translateService.instant('PROGRAM-LEAD');
                      break;
                    case 'template':
                      propertyNameText = this.translateService.instant('TEMPLATE');
                      break;
                    default:
                      propertyNameText = e;
                      break;
                  }
                  // Property Value
                  let newValueText = null;
                  let previousValueText = null;
                  switch (e)
                  {
                    case 'active':
                    case 'template':
                      {
                        newValueText = d[e] ? this.translateService.instant('YES') : this.translateService.instant('NO');
                        if (data.length === 2)
                        {
                          previousValueText = data[1][e] ? this.translateService.instant('YES') : this.translateService.instant('NO');
                        }
                        break;
                      }
                  }
                  this.data.push({
                    propertyName: propertyNameText,
                    newValue: newValueText !== null ? newValueText : d[e],
                    previousValue: previousValueText !== null ? (data.length === 2 ? previousValueText : '') : (data[1] ? data[1][e] : '')
                  });
                }
              }
            });
          }
          index++;
        });
        this.view.setData(this.data);
        this.view.setSort([{ field: 'propertyName', dir: 'asc' }]);
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Get audit details for audit_program_groups
   **/
  public getAuditProgramGroupsDetails(): void
  {
    this.auditProgramService.getAuditProgramDetails('program/groups', this.currentProgramId, this.currentRevisionId, this.currentRevisionType).subscribe({
      next: (data: any) =>
      {
        this.count = data.length;
        this.data = [];
        data.forEach((d: any) =>
        {
          const value = {
            propertyName: this.translateService.instant('GROUP-NAME'),
            newValue: d.name,
            previousValue: ''
          }
          this.data.push(value);
        });
        this.view.setData(this.data);
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }

  /**
   * Get audit details for audit_program_metric
   **/
  public getAuditProgramMetricsDetails(): void
  {
    this.auditProgramService.getAuditProgramDetails('program/metrics', this.currentProgramId, this.currentRevisionId, this.currentRevisionType).subscribe({
      next: (data) =>
      {
        this.count = data.length;
        this.data = [];
        let index = 0;
        data.forEach((d: any) =>
        {
          if (index === 0)
          {
            Object.keys(d).forEach(e =>
            {
              let propertyNameText = null;
              if (e !== 'id' && e !== 'revisionId')
              {
                if ((!this.currentChangesOnly) ||
                  ((this.currentChangesOnly && data.length === 2 && d[e] !== data[1][e])) || /* Update */
                  ((this.currentChangesOnly && data.length === 1)))                          /* Create */
                {
                  // Property Name
                  switch (e)
                  {
                    case 'target':
                      propertyNameText = this.translateService.instant('TARGET');
                      break;
                    case 'denominator':
                      propertyNameText = d.denominatorDescription;
                      break;
                    case 'numerator':
                      propertyNameText = d.numeratorDescription;
                      break;
                    case 'rate':
                      propertyNameText = this.translateService.instant('RATE');
                      break;
                    case 'metricName':
                      propertyNameText = this.translateService.instant('PROGRAM-METRIC');
                      break;
                    default:
                      propertyNameText = e;
                      break;
                  }
                  // Property Value
                  if (e !== 'denominatorDescription' && e !== 'numeratorDescription')
                  {
                    this.data.push({
                      propertyName: propertyNameText,
                      newValue: d[e],
                      previousValue: data[1] ? data[1][e] : ''
                    });
                  }
                }
              }
            });
          }
          index++;
        });
        this.view.setData(this.data);
        this.view.setSort([{ field: 'propertyName', dir: 'asc' }]);
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }

  private initGridColumns(res: any): void
  {
    // currentAuditType: 0=Program, 1=Leads/Groups, 2=Metrics
    // currentRevisionType: 0=Create, 1=Update, 2=Delete
    this.columnsConfig = [
      {
        field: 'propertyName', title: res['PROPERTY-NAME'], titleKeys: ['PROPERTY-NAME'],
        width: 20, sortable: true, hidden: false, filterable: this.currentEnableAdvanced, dataType: 'string'
      },
      {
        field: 'newValue',
        title: this.currentAuditType === 1 && ((this.currentRevisionType === 0) || (this.currentRevisionType === 2)) ? res.VALUE : res['NEW-VALUE'],
        titleKeys: this.currentAuditType === 1 && ((this.currentRevisionType === 0) || (this.currentRevisionType === 2)) ? res.VALUE : res['NEW-VALUE'],
        width: 40, sortable: true, hidden: false, filterable: this.currentEnableAdvanced, dataType: 'string'
      },
      {
        field: 'previousValue', title: res['PREVIOUS-VALUE'], titleKeys: ['PREVIOUS-VALUE'],
        width: 40, sortable: true, hidden: this.currentRevisionType === 0 || ((this.currentAuditType === 1) && (this.currentRevisionType === 2)), filterable: this.currentEnableAdvanced, dataType: 'string'
      }
    ];
  }

  private initGridColumnsAndLoadData(): void
  {
    this.translateService.get(['PROPERTY-NAME', 'NEW-VALUE', 'PREVIOUS-VALUE', 'VALUE', 'NAME', 'NEW-NAME']).subscribe({
      next: (res) =>
      {
        this.initGridColumns(res);
        if (this.currentAuditType === 0)
        {
          this.getAuditProgramDetails();
        }
        else
          if (this.currentAuditType === 1)
          {
            this.getAuditProgramGroupsDetails();
          }
          else
            if (this.currentAuditType === 2)
            {
              this.getAuditProgramMetricsDetails();
            }
      }
    });
  }
}
